<template>
  <nav class="menu-tab-bar" aria-label="Menu navigation">
    <div class="menu-tab-bar__bg" aria-hidden="true"></div>
    <button
      v-for="tab in visibleTabs"
      :key="tab.id"
      type="button"
      class="menu-tab-bar__tab"
      :class="{ 'is-active': isActive(tab.page) }"
      @click="selectTab(tab.page)"
    >
      <span class="menu-tab-bar__icon-wrap">
        <img class="menu-tab-bar__icon" :src="tab.icon" alt="" />
        <span v-if="tab.badge" class="menu-tab-bar__badge">{{ tab.badge }}</span>
      </span>
      <span class="menu-tab-bar__label">{{ $t(tab.label) }}</span>
    </button>
    <div class="menu-tab-bar__lines" aria-hidden="true">
      <div
        class="menu-tab-bar__line-active"
        :style="{ width: activeLineWidth, left: activeLineLeft }"
      ></div>
      <div class="menu-tab-bar__line-inactive"></div>
    </div>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex';
import { PopupPage } from '@/utils/index.model';

export default {
  props: {
    activePage: {
      type: String,
      required: true,
    },
    scheduleCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      PopupPage,
    };
  },
  computed: {
    ...mapGetters('general', ['config']),
    visibleTabs() {
      const tabs = [
        {
          id: 'history',
          page: PopupPage.history,
          label: 'History',
          icon: require('@/assets/svg/menu-tabs/history.svg'),
        },
        {
          id: 'help',
          page: PopupPage.help,
          label: 'Help',
          icon: require('@/assets/svg/menu-tabs/info.svg'),
        },
      ];

      if (this.config.enableJackpot) {
        tabs.push({
          id: 'jackpot',
          page: PopupPage.jackpotHelp,
          label: 'Jackpot',
          icon: require('@/assets/svg/menu-tabs/jackpot.svg'),
        });
      }

      tabs.push({
        id: 'schedule',
        page: PopupPage.schedule,
        label: 'Schedule',
        icon: require('@/assets/svg/menu-tabs/schedule.svg'),
        badge: this.scheduleCount > 0 ? this.scheduleCount : null,
      });

      tabs.push({
        id: 'alert',
        page: PopupPage.notification,
        label: 'Alert',
        icon: require('@/assets/svg/menu-tabs/alert.svg'),
      });

      return tabs;
    },
    activeTabIndex() {
      const idx = this.visibleTabs.findIndex((tab) => this.isActive(tab.page));
      return idx >= 0 ? idx : 0;
    },
    activeLineWidth() {
      return `${100 / this.visibleTabs.length}%`;
    },
    activeLineLeft() {
      return `${(100 / this.visibleTabs.length) * this.activeTabIndex}%`;
    },
  },
  methods: {
    isActive(page) {
      if (page === PopupPage.history) {
        return [PopupPage.history, PopupPage.statement, PopupPage.betDetail].includes(this.activePage);
      }
      if (page === PopupPage.help) {
        return [PopupPage.help, PopupPage.tnc].includes(this.activePage);
      }
      if (page === PopupPage.jackpotHelp) {
        return [PopupPage.jackpotHelp, PopupPage.jackpotPhysicalHelp].includes(this.activePage);
      }
      if (page === PopupPage.schedule) {
        return [PopupPage.schedule, PopupPage.scheduleDetail].includes(this.activePage);
      }
      return this.activePage === page;
    },
    selectTab(page) {
      this.$emit('select', page);
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-tab-bar {
  position: relative;
  display: flex;
  width: 100%;
  height: 56px;
  flex-shrink: 0;
  overflow: hidden;

  &__bg {
    position: absolute;
    inset: 0;
    background-color: #10233a;
    border-radius: 16px 16px 0 0;
    pointer-events: none;
  }

  &__tab {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 4px 10px;
    border: 0;
    background: transparent;
    cursor: pointer;
    min-width: 0;

    &.is-active {
      background: linear-gradient(180deg, rgba(79, 58, 36, 0.55) 0%, rgba(16, 35, 58, 0) 100%);

      .menu-tab-bar__label {
        color: #efc87f;
        font-weight: 700;
      }

      .menu-tab-bar__icon {
        filter: none;
        opacity: 1;
      }
    }
  }

  &__icon-wrap {
    position: relative;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon {
    width: 20px;
    height: 20px;
    opacity: 0.85;
    filter: brightness(0) saturate(100%) invert(84%) sepia(12%) saturate(746%) hue-rotate(169deg) brightness(96%)
      contrast(91%);
  }

  &__tab.is-active &__icon {
    filter: brightness(0) saturate(100%) invert(80%) sepia(34%) saturate(638%) hue-rotate(358deg) brightness(99%)
      contrast(89%);
  }

  &__badge {
    position: absolute;
    top: -5px;
    right: -8px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 7px;
    background: #e53935;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    color: #b2d6ea;
    text-transform: uppercase;
    white-space: nowrap;
  }

  &__lines {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    pointer-events: none;
    z-index: 2;
  }

  &__line-inactive {
    position: absolute;
    inset: 0;
    background: rgba(106, 145, 167, 0.35);
  }

  &__line-active {
    position: absolute;
    top: 0;
    bottom: 0;
    background: linear-gradient(90deg, #c9a24d 0%, #efc87f 50%, #c9a24d 100%);
    transition: left 0.2s ease;
  }
}
</style>
